/**
 * useBandwidthAlertCount — polls the open-alert count for the nav
 * badge on the Bandwidth Alerts link. 30s cadence, soft-fails.
 *
 * Backend already scopes the response so only platform users see a
 * non-zero count (customer/vendor users get 0 regardless).
 */
import { useEffect, useRef, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { fetchBandwidthAlertCount } from "../services/bandwidthApi";

const POLL_MS = 30_000;

export function useBandwidthAlertCount(): number {
  const { token, isAuthenticated } = useAuth();
  const [count, setCount] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    cancelled.current = false;
    if (!isAuthenticated || !token) {
      setCount(0);
      return;
    }
    const poll = (): void => {
      fetchBandwidthAlertCount(token)
        .then((d) => {
          if (cancelled.current) return;
          setCount(d.open_count ?? 0);
        })
        .catch(() => {
          // Leave stale count in place — better than blanking on a hiccup.
        });
    };
    poll();
    const id = window.setInterval(poll, POLL_MS);
    return () => {
      cancelled.current = true;
      window.clearInterval(id);
    };
  }, [token, isAuthenticated]);

  return count;
}
